// FATE override 2026-09-18: accumulate quantity intent; preserve confirmed pricing.
import { cartSync, cartRequest, readCart, quantityDeltaStep } from '@theme/fate-cart-sync';
import { Component } from '@theme/component';
import {
  fetchConfig,
  resetShimmer,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
// Module scope survives row/component replacement by section hydration.
const quantityJobs = new Map();
function restorePendingQuantities() {
  for (const row of document.querySelectorAll('.cart-items__table-row[data-key]')) {
    const job = quantityJobs.get(row.dataset.key);
    if (job) row.setAttribute('aria-busy','true'); else row.removeAttribute('aria-busy');
    if (job) {
      const input = row.querySelector('cart-quantity-selector-component input');
      if (input) input.value = String(job.requested);
      row.querySelector('cart-quantity-selector-component')?.updateButtonStates?.();
    }
  }
}
class CartItemsComponent extends Component {
  #onChange = this.#onQuantityChange.bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const row = this.refs.cartItemRows[config.line - 1];
    if (!row?.dataset.key) return;
    const id = row.dataset.key;
    const requested = Number(config.quantity);
    if (!Number.isSafeInteger(requested) || requested < 0) return;
    const existing = quantityJobs.get(id);
    if (existing) {
      existing.requested = requested;
      restorePendingQuantities();
      return existing.promise;
    }
    const input = row.querySelector('cart-quantity-selector-component input');
    const identity = JSON.parse(row.dataset.cartIdentity || 'null');
    const fixedQuantity = row.querySelector('.fate-gift-line__qty')?.textContent;
    const job = {requested, applied:input ? Number(input.defaultValue) : fixedQuantity ? Number(fixedQuantity) : null};
    quantityJobs.set(id,job);
    restorePendingQuantities();
    const marker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    job.promise = cartSync.run(async () => {
      // Fixed short collection window: feedback is immediate, checkout waits for
      // this lane, and repeated taps become a single requested quantity.
      await new Promise(resolve => setTimeout(resolve,160));
      let cart = await readCart();
      if (job.applied === null) {
        const item = cart.items.find(item => item.key === id);
        if (!item) throw Object.assign(new Error('Actualiza el carrito antes de cambiar esta línea.'),{cartOutcome:'read'});
        job.applied = item.quantity;
      }
      do {
        const target = job.requested;
        let remaining = target - job.applied;
        while (remaining !== 0) {
          const step = quantityDeltaStep(cart,id,identity,remaining);
          const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(n=>n.dataset.sectionId))].slice(0,5);
          const body = JSON.stringify({id:step.id,quantity:step.quantity,sections,sections_url:location.pathname});
          cart = await cartRequest(Theme.routes.cart_change_url,fetchConfig('json',{body})).then(r=>r.json());
          if (cart.errors) throw new Error(cart.errors);
          remaining -= step.applied;
        }
        job.applied = target;
        // Do not overwrite newer intent with intermediate server HTML.
      } while (job.requested !== job.applied);
      if (quantityJobs.get(id) === job) quantityJobs.delete(id);
      this.#updateQuantitySelectors(cart);
      await window.__fateRenderVerifiedCart(cart,{source:'cart-items-component'});
      this.#updateCartQuantitySelectorButtonStates();
      return cart;
    }).catch(async error => {
      if (quantityJobs.get(id) === job) quantityJobs.delete(id);
      // Reconcile even after 422/transport failure, but never replay the write.
      try { await window.__fateRecoverCartWrite?.(); } catch (_) {}
      window.__fateCartError?.(error);
    }).finally(() => {
      if (quantityJobs.get(id) === job) quantityJobs.delete(id);
      resetShimmer(this);
      restorePendingQuantities();
      cartPerformance.measureFromMarker(marker);
    });
    return job.promise;
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.detail?.rendered) return;
    if (event instanceof DiscountUpdateEvent) {
      const html = event.detail.resource?.sections?.[this.sectionId];
      if (html) morphSection(this.sectionId, html, this.isDrawer ? 'hydration' : 'full');
      return;
    }
    if (event.target === this || event.detail?.data?.rendered) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml, this.isDrawer ? 'hydration' : 'full');

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    const quantities = new Map();
    for (const item of updatedCart.items) quantities.set(String(item.variant_id),(quantities.get(String(item.variant_id)) || 0) + item.quantity);
    for (const [variantId, quantity] of quantities) {
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
